import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Card3D } from "@/components/Card3D";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { fixedSlots } from "@/lib/listIdentity";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, Check } from "lucide-react";

/**
 * PackOpeningModal
 * props:
 *  - cards: array of card objects (1..N)
 *  - packName: string
 *  - onClose: called after the user finishes acquiring (final "Acquire")
 *
 * Flow:
 *  stage 0-2: pack chamber + shake + tear open
 *  stage 3: card reveal. User sees one card at a time. If multiple cards, "Next" advances. On the last card, "Acquire" closes.
 */
export function PackOpeningModal({ cards, packName, onClose }) {
  const list = Array.isArray(cards) ? cards : [cards].filter(Boolean);
  const total = list.length;
  const [stage, setStage] = useState(0);
  const [index, setIndex] = useState(0);
  // Track the furthest card the player has revealed; going "back" and then
  // "forward" should never require watching an already-seen reveal animation.
  const [maxRevealed, setMaxRevealed] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 500);
    const t2 = setTimeout(() => setStage(2), 1400);
    const t3 = setTimeout(() => setStage(3), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const isLast = index >= total - 1;
  const current = list[index];
  const allSeen = maxRevealed >= total - 1;

  const goTo = (i) => {
    if (i < 0 || i >= total) return;
    setIndex(i);
    setMaxRevealed((m) => Math.max(m, i));
  };
  const back = () => goTo(index - 1);
  const next = () => {
    if (isLast && allSeen) {
      onClose?.();
    } else if (isLast) {
      // Impossible: isLast implies index === total-1 which sets maxRevealed=total-1 → allSeen.
      onClose?.();
    } else {
      goTo(index + 1);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent className="max-w-3xl border-white/10 bg-[hsl(222_22%_5%)] overflow-hidden p-0">
        <VisuallyHidden asChild>
          <DialogTitle>Pack opening: {packName}</DialogTitle>
        </VisuallyHidden>
        <div className="relative min-h-[680px] flex flex-col items-center justify-center p-8">
          {/* Ambient sparkles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 gridline opacity-20" />
            {fixedSlots(30).map(({ id, index: i }) => (
              <span
                key={id}
                className="sparkle"
                style={{
                  position: "absolute",
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  width: 2 + Math.random() * 3,
                  height: 2 + Math.random() * 3,
                  animationDelay: `${Math.random() * 2}s`,
                }}
              />
            ))}
          </div>

          <div className="relative z-10 flex flex-col items-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-cyan-300/80 mb-3">
              {packName} · OPENING {total > 1 && stage === 3 ? `· ${index + 1} / ${total}` : ""}
            </div>

            <AnimatePresence mode="wait">
              {stage < 3 ? (
                <motion.div
                  key="pack"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{
                    opacity: 1,
                    scale: stage === 1 ? [1, 1.04, 0.97, 1.04, 1] : stage === 2 ? 1.18 : 1,
                    rotate: stage === 1 ? [-1.5, 1.5, -2, 1.5, 0] : 0,
                  }}
                  exit={{ opacity: 0, scale: 1.3, filter: "blur(8px)" }}
                  transition={{ duration: stage === 1 ? 0.9 : 0.4 }}
                >
                  <div className={`pack-wrapper rarity-${current?.rarity || "common"} relative rounded-[14px] w-[260px] sm:w-[320px] aspect-[2.5/3.5] overflow-hidden card-aura-strong`}>
                    <motion.div
                      className="pack-half pack-half-left absolute inset-y-0 left-0 w-1/2 bg-gradient-to-br from-[hsl(222_22%_8%)] via-[hsl(220_30%_14%)] to-black border-r border-white/10"
                      animate={stage >= 2 ? { x: "-110%", rotate: -12, opacity: 0 } : {}}
                      transition={{ duration: 0.7, ease: [0.7, 0, 0.3, 1] }}
                    >
                      <div className="absolute inset-0 holo-scan" />
                      <div className="foil-overlay" />
                      <div className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.3em] text-cyan-300/80">PLAY</div>
                      <div className="absolute bottom-3 left-3 right-0 font-display text-4xl text-white/85 tracking-[0.08em]">{packName?.split(" ")[0] || "PACK"}</div>
                    </motion.div>
                    <motion.div
                      className="pack-half pack-half-right absolute inset-y-0 right-0 w-1/2 bg-gradient-to-bl from-[hsl(222_22%_10%)] via-[hsl(220_30%_14%)] to-black border-l border-white/10"
                      animate={stage >= 2 ? { x: "110%", rotate: 12, opacity: 0 } : {}}
                      transition={{ duration: 0.7, ease: [0.7, 0, 0.3, 1] }}
                    >
                      <div className="absolute inset-0 holo-scan" />
                      <div className="foil-overlay" />
                      <div className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.3em] text-cyan-300/80">DEN</div>
                      <div className="absolute bottom-3 right-3 font-display text-4xl text-white/85 tracking-[0.08em] text-right">{packName?.split(" ").slice(1).join(" ") || ""}</div>
                    </motion.div>
                    {stage === 2 && (
                      <motion.div
                        className="absolute inset-0"
                        style={{ background: "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.95), rgba(255,255,255,0) 60%)" }}
                        initial={{ opacity: 0, scale: 0.4 }}
                        animate={{ opacity: [0, 1, 0], scale: [0.4, 1.4, 1.8] }}
                        transition={{ duration: 0.6 }}
                      />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <Sparkles className="h-10 w-10 text-white/40 animate-pulse" />
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={`card-${index}`}
                  initial={{ opacity: 0, scale: 0.7, rotateY: -35 }}
                  animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                  exit={{ opacity: 0, scale: 0.85, x: -60 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                  {current && <Card3D card={{ ...current, locked: false }} size="lg" interactive alwaysOwner previewSide="left" />}
                </motion.div>
              )}
            </AnimatePresence>

            {stage === 3 && current && (
              <motion.div
                key={`meta-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="text-center mt-6"
              >
                <div className={`rarity-${current.rarity} font-display text-3xl text-white tracking-[0.08em]`} style={{ textShadow: `0 0 24px var(--aura)` }}>
                  {current.title}
                </div>
                <div className={`rarity-${current.rarity} font-mono text-xs uppercase tracking-[0.3em] mt-1`} style={{ color: "var(--label)" }}>{current.rarity}</div>
                <div className="font-mono text-xs text-white/40 mt-1">{current.serial}</div>
                {current.edition_number != null && (
                  <div className="font-mono text-[11px] text-cyan-300 mt-1.5 inline-block px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/30" data-testid="pack-edition-number">
                    EDITION #{current.edition_number}
                  </div>
                )}
              </motion.div>
            )}

            {stage === 3 && (
              <motion.div
                key={`actions-${index}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex flex-col items-center gap-3 mt-6"
              >
                {total > 1 && (
                  <div className="flex items-center gap-1.5" data-testid="pack-open-dots">
                    {list.map((c, i) => {
                      const seen = i <= maxRevealed;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          data-testid={`pack-open-dot-${i}`}
                          onClick={() => seen && goTo(i)}
                          disabled={!seen}
                          className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-cyan-300" : seen ? "w-3 bg-white/40 hover:bg-cyan-300/70 cursor-pointer" : "w-3 bg-white/15 cursor-not-allowed"}`}
                          aria-label={`Card ${i + 1} of ${total}`}
                        />
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {index > 0 && (
                    <Button
                      data-testid="pack-open-back-button"
                      onClick={back}
                      variant="outline"
                      className="border-white/20 bg-white/[0.04] text-white/80 hover:bg-white/10 hover:text-white px-4"
                    >
                      <ArrowLeft className="mr-1 h-4 w-4" /> Back
                    </Button>
                  )}
                  <Button
                    data-testid={isLast && allSeen ? "pack-open-acquire-button" : "pack-open-next-button"}
                    onClick={next}
                    className="bg-cyan-400 text-black hover:bg-cyan-300 px-6"
                  >
                    {isLast && allSeen ? (
                      <>Acquire <Check className="ml-1 h-4 w-4" /></>
                    ) : (
                      <>Next <ArrowRight className="ml-1 h-4 w-4" /></>
                    )}
                  </Button>
                </div>
                {total > 1 && (
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {isLast && allSeen ? "Add all to vault" : `${total - maxRevealed - 1} more to reveal · tap a dot to revisit`}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
